/**
 * useMatchRecorder — captures a MediaStream into S3 via multipart upload.
 *
 * Strategy
 * ──────────
 * • Records 5-min chunks (≈56 MB each at 1.5 Mbps), each chunk = one S3 part.
 * • Each chunk is queued in IndexedDB BEFORE upload starts, so a tab crash
 *   doesn't lose the data — on next mount we resume the upload.
 * • Backend signs one PUT URL per part (15-min TTL); browser PUTs the blob
 *   straight to S3, no transit through Django, no egress cost.
 * • Per-ball clock = ms elapsed since recording started, paused while paused.
 *
 * Storage tuning (already optimized for cost):
 *   videoBitsPerSecond: 1_500_000        // 1.5 Mbps
 *   constraints: 1280x720 @ 30fps        // 720p
 *   audio: false                          // -15% storage; cricket needs no crowd noise
 *   container: video/mp4 H.264 if Chrome supports it, else WebM VP9
 *
 * Public surface:
 *   const r = useMatchRecorder({ matchId, stream });
 *   r.start()   r.stop()   r.elapsedMs   r.state   r.uploadProgress
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  startRecording, presignPart, completeRecording, abortRecording,
  presignLiveChunk, processLiveChunk, getActiveRecording,
  finalizeRecording, getRecordingState,
} from "../api/recordings.api";
import {
  saveLiveChunk, clearLiveChunks, trimOldestChunks,
} from "./useLiveChunks";

// 5-sec chunks (v2 recorder, 2026-05-21).
//
// Previous design used 60-sec chunks because each chunk was an S3
// multipart-upload part and S3 imposes a 5 MiB minimum per part.
// v2 drops multipart entirely — each chunk lands as its own
// standalone S3 object via PUT, so the 5 MiB floor no longer applies.
// 5 × 1.5 Mbps ≈ 940 KB per chunk — small enough to land in <1s on
// typical broadband, large enough to be a self-contained MP4 (the
// first frames of each timeslice include the ftyp+moov+mdat needed
// for standalone playback).
//
// Why 5s and not 1s? At 5s the per-ball slicer typically needs 2-3
// chunks (a 10s ball ≈ 2-3 × 5s); at 1s it'd need 10-15 chunks per
// ball, which inflates the S3 LIST + concat cost. 5s is the sweet
// spot between latency (clips appear ~10-15s after the ball) and
// per-slice overhead.
const CHUNK_MS = 5 * 1000;
// v2 has no S3 minimum-part-size constraint. Keep these constants
// around for the legacy v1 resume path that still needs them.
const S3_MIN_PART_BYTES = 5 * 1024 * 1024;
const SUSPICIOUS_CHUNK_BYTES = 50_000;
// Soft cap on the in-IDB live mirror — long matches (Test sessions) can
// blow past 1.5 GB and trip IDB quotas on some browsers. After the cap,
// new chunks still upload to S3, we just stop refreshing the local
// mirror; the slicer fills in the rest after Stop.
const LOCAL_MIRROR_MAX_BYTES = 1.5 * 1024 * 1024 * 1024;

const IDB_DB   = "zcd_recorder_v1";
const IDB_STORE = "chunks";

// ── tiny IDB wrapper: persist {recordingId, partNumber, blob, etag?} ──
function idbOpen() {
  return new Promise((res, rej) => {
    const r = indexedDB.open(IDB_DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(IDB_STORE, { keyPath: "key" });
    r.onsuccess = () => res(r.result);
    r.onerror   = () => rej(r.error);
  });
}
async function idbPut(rec)    { const db = await idbOpen(); const tx = db.transaction(IDB_STORE, "readwrite"); tx.objectStore(IDB_STORE).put(rec); return new Promise(r => tx.oncomplete = r); }
async function idbDelete(key) { const db = await idbOpen(); const tx = db.transaction(IDB_STORE, "readwrite"); tx.objectStore(IDB_STORE).delete(key); return new Promise(r => tx.oncomplete = r); }
async function idbAllForRec(recId) {
  const db = await idbOpen();
  return new Promise((res) => {
    const out = [];
    const tx  = db.transaction(IDB_STORE, "readonly");
    tx.objectStore(IDB_STORE).openCursor().onsuccess = (e) => {
      const c = e.target.result;
      if (!c) return res(out);
      if (c.value.recordingId === recId) out.push(c.value);
      c.continue();
    };
  });
}

// pick the best mime the browser will MediaRecord
function pickMime() {
  const candidates = [
    "video/mp4;codecs=avc1.640028",          // 720p high profile, MP4 (Chrome ≥110)
    "video/mp4;codecs=avc1.42E01E",          // baseline H.264
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
  ];
  for (const m of candidates) {
    if (window.MediaRecorder?.isTypeSupported?.(m)) return m;
  }
  return "";
}

export function useMatchRecorder({ matchId, stream, tier = "B2C" }) {
  const recRef        = useRef(null);                 // MediaRecorder (B2C) OR IVSBroadcastClient (B2B)
  const recordingId   = useRef(null);                 // backend MatchRecording.id
  const partCounter   = useRef(0);                    // S3 PartNumber (1-based, B2C only)
  const uploadedRef   = useRef([]);                   // [{PartNumber, ETag}], B2C only
  const inFlightRef   = useRef(new Set());            // partNumbers currently uploading
  const pendingProm   = useRef([]);                   // Promise[] for in-flight uploads (settled by stop)
  const isStoppingRef = useRef(false);                // true between stop() and the final dataavailable
  // Local mirror of every chunk we've recorded. Stitched into IDB as the
  // match-id-keyed master blob (via saveLocalMatchFile) so QC's
  // BallVideoPlayer can play just-recorded clips IMMEDIATELY — before
  // S3 upload, complete-multipart, or the slicer have run. The same
  // path is already used for "Upload from computer" file-mode review,
  // so QC needs zero changes to read from it.
  const liveBytes     = useRef(0);                    // running total of bytes written to live IDB store (for the soft cap)
  const startedAtRef  = useRef(null);                 // performance.now() at start
  const accumMsRef    = useRef(0);                    // elapsed across pauses
  const totalBytes    = useRef(0);                    // sum of uploaded chunk sizes — sent to /complete/
  const ivsRef        = useRef(null);                 // IVSBroadcastClient instance (B2B)
  const cycleTimerRef = useRef(null);                 // setTimeout handle for v2 stop/start cycle
  const cyclePendingRef = useRef(false);              // true between scheduling + actual cycle start — prevents double-spawn
  const lastCycleAtRef = useRef(0);                   // performance.now() of last cycle start — enforces min interval
  const consecutiveCycleErrorsRef = useRef(0);        // count cycles that produced 0 data, bail after N
  // recorder_version from the backend's start response. v1 = legacy
  // multipart, v2 = standalone-chunk PUTs + EC2 master concat. The
  // uploadChunk + stop paths branch on this.
  const recorderVersionRef = useRef(2);
  // Finalize-time poll state — populated by stop() so the SPA's Stop
  // overlay can render the master concat's progress bar.
  const [finalizeState, setFinalizeState] = useState(null);  // {phase, pct, error} | null

  const [state, setState]                = useState("idle"); // idle|starting|recording|stopping|complete|error
  const [elapsedMs, setElapsedMs]        = useState(0);
  const [uploadProgress, setUploadProgress] = useState({ uploaded: 0, queued: 0 });
  const [error, setError]                = useState("");

  // tick the elapsed clock 5×/sec while recording
  //
  // Round to integer ms — downstream this value goes into
  // payload.video_start_ms / video_end_ms which the DRF backend
  // serializer types as IntegerField. performance.now() returns a
  // float (microsecond precision) and an unrounded value caused
  // ball saves to 400 with "A valid integer is required." every
  // time the operator was actively recording.
  useEffect(() => {
    if (state !== "recording") return;
    const id = setInterval(() => {
      setElapsedMs(Math.round(
        accumMsRef.current + (performance.now() - startedAtRef.current),
      ));
    }, 200);
    return () => clearInterval(id);
  }, [state]);

  // upload one queued chunk (idempotent — uses idbAllForRec on resume)
  //
  // v2 path (the only path for new recordings): each chunk lands as a
  // standalone S3 object at live-chunks/<recording_id>/part-<N>.mp4
  // via a single PUT. Right after the PUT lands, we fire
  // /process-live-chunk/<N>/ — the backend then slices per-ball clips
  // for any balls whose final chunk is this one (or earlier). No
  // multipart upload, no S3 5 MiB minimum, no zombie UPLOADING rows.
  //
  // v1 legacy path (resumed-old-recording): retains the multipart PUT
  // path so an in-flight v1 recording can finish on the same protocol
  // it started. Selected by recorderVersionRef.
  const uploadChunk = useCallback(async (recId, partNumber, blob) => {
    inFlightRef.current.add(partNumber);
    setUploadProgress((p) => ({ ...p, queued: p.queued + 1 }));
    try {
      const isV2 = (recorderVersionRef.current || 2) >= 2;

      if (isV2) {
        // V2: standalone-PUT only. No multipart, no ETag tracking.
        const { url, content_type: ct } = await presignLiveChunk(recId, partNumber, "mp4");
        const res = await fetch(url, {
          method: "PUT",
          body:   blob,
          headers: { "Content-Type": ct || "video/mp4" },
        });
        if (!res.ok) throw new Error(`S3 PUT failed ${res.status}`);
        totalBytes.current += blob.size;
        await idbDelete(`${recId}::${partNumber}`);
        setUploadProgress((p) => ({ uploaded: p.uploaded + 1, queued: p.queued - 1 }));

        // Trigger per-ball slicing — fire-and-forget. The backend
        // walks balls whose video_end_ms is now covered, runs EC2
        // ffmpeg concat for multi-chunk spans, PUTs the per-ball MP4.
        // If this fails the next chunk's process call will retry the
        // same balls (idempotent on the backend).
        processLiveChunk(recId, partNumber).catch((e) => {
          console.warn(
            "process-live-chunk part=%d failed: %s — will retry on next chunk",
            partNumber, e?.response?.data?.detail || e?.message || e,
          );
        });
      } else {
        // V1 legacy: dual PUT (multipart part + standalone mirror) so
        // resumed-from-v1 recordings still finalize via /complete/.
        const [{ url: partUrl }, { url: liveUrl, content_type: liveCt }] = await Promise.all([
          presignPart(recId, partNumber),
          presignLiveChunk(recId, partNumber, "mp4").catch(() => ({ url: null })),
        ]);
        const partPut = fetch(partUrl, {
          method: "PUT",
          body:   blob,
          headers: { "Content-Type": "application/octet-stream" },
        });
        const livePut = liveUrl
          ? fetch(liveUrl, {
              method: "PUT",
              body:   blob,
              headers: { "Content-Type": liveCt || "video/mp4" },
            }).catch(() => null)
          : Promise.resolve(null);

        const [res, liveRes] = await Promise.all([partPut, livePut]);
        if (!res.ok) throw new Error(`S3 PUT failed ${res.status}`);
        const etag = res.headers.get("ETag")?.replaceAll('"', "") || "";
        uploadedRef.current.push({ PartNumber: partNumber, ETag: etag });
        totalBytes.current += blob.size;
        await idbDelete(`${recId}::${partNumber}`);
        setUploadProgress((p) => ({ uploaded: p.uploaded + 1, queued: p.queued - 1 }));
        if (liveRes && liveRes.ok) {
          processLiveChunk(recId, partNumber).catch(() => {});
        }
      }
    } finally {
      inFlightRef.current.delete(partNumber);
    }
  }, []);

  // wire MediaRecorder ondataavailable → IDB → S3
  const onDataAvailable = useCallback((e) => {
    if (!e.data || !e.data.size) return;
    const size = e.data.size;
    const stopping = isStoppingRef.current;

    const isV2 = (recorderVersionRef.current || 2) >= 2;

    if (size < SUSPICIOUS_CHUNK_BYTES) {
      // Tiny blob = MediaRecorder emitted just the init segment because
      // no frames were available in the timeslice — camera off, track
      // ended, tab throttled. Warn either way.
      console.warn(
        "Suspiciously small chunk: %d bytes (part %d). MediaRecorder may "
        + "not be capturing frames — check camera + tab visibility.",
        size, partCounter.current + 1,
      );
    }

    // V1 only: S3 multipart hard rule — every part except the LAST
    // must be >=5 MiB. Drop sub-floor chunks so /complete/ doesn't
    // EntityTooSmall the whole upload. V2 uses standalone PUTs with
    // no minimum, so tiny chunks (~940 KB at 5s × 1.5 Mbps is
    // normal) are uploaded as-is.
    if (!isV2 && !stopping && size < S3_MIN_PART_BYTES) {
      console.warn(
        "v1 path: dropping mid-recording chunk %d (%d bytes) — below "
        + "S3's 5 MiB part floor. Likely a low-motion segment.",
        partCounter.current + 1, size,
      );
      return;
    }

    partCounter.current += 1;
    const partNo = partCounter.current;
    const recId  = recordingId.current;
    const key    = `${recId}::${partNo}`;

    // Mirror the chunk into the per-chunk IDB store so QC can play
    // just-recorded clips immediately, without waiting for Stop +
    // S3 multipart complete + slicer.
    //
    // We store chunks INDIVIDUALLY (not concatenated) because Chrome's
    // MediaRecorder emits each timeslice as a self-contained MP4
    // (its own ftyp + moov pointing at its own mdat). A naive concat
    // makes the browser ignore everything past the first chunk's
    // moov box — that was the "loading forever" QC bug. With separate
    // chunks, the QC player picks the ONE chunk that covers the ball's
    // video_start_ms and seeks into its local timeline.
    //
    // Fire-and-forget so a slow disk doesn't backpressure uploads.
    if (matchId && liveBytes.current < LOCAL_MIRROR_MAX_BYTES) {
      liveBytes.current += size;
      // Log so the operator can verify (via DevTools) that chunks are
      // actually landing in the local store — a previous bug class was
      // "chunks save but as init-segment-only stubs" which left QC
      // playback stuck on 0:00 duration.
      console.info(
        "[recorder] live chunk saved: part=%d size=%d bytes win=%dms-%dms stopping=%s",
        partNo, size, (partNo - 1) * CHUNK_MS, partNo * CHUNK_MS, stopping,
      );
      saveLiveChunk({
        matchId,
        partNumber: partNo,
        chunkMs:    CHUNK_MS,
        blob:       e.data,
      })
        .then(() => trimOldestChunks(matchId))
        .catch((err) => console.warn("live chunk IDB save failed", err));
    }

    // Track the upload promise so stop() can await it before completing.
    const p = (async () => {
      try {
        await idbPut({ key, recordingId: recId, partNumber: partNo, blob: e.data });
        await uploadChunk(recId, partNo, e.data);
      } catch (err) {
        // chunk stays in IDB; will retry on next start() / page reload
        console.warn("chunk upload failed, kept in IDB", err);
      }
    })();
    pendingProm.current.push(p);
  }, [uploadChunk, matchId]);

  // V2 stop/start cycle: each chunk is its own complete MediaRecorder
  // session, producing a self-contained MP4 with ftyp+moov+mdat. This
  // replaces the legacy single-recorder-with-timeslice approach where
  // chunks 2+ had no init segment (just moof+mdat), making them
  // individually undecodable by ffmpeg. Trade-off: ~30-100ms gap per
  // cycle (the time to stop one MediaRecorder + spawn the next),
  // visible only in the assembled master MP4 as a brief micro-stutter
  // every 5s. The operator's LIVE camera preview is unaffected — that
  // reads from the MediaStream directly.
  const handleV2ChunkBlob = useCallback((partNo, blob) => {
    const size = blob.size;
    if (matchId && liveBytes.current < LOCAL_MIRROR_MAX_BYTES) {
      liveBytes.current += size;
      console.info(
        "[recorder v2] chunk %d  size=%d bytes  type=%s",
        partNo, size, blob.type || "?",
      );
      saveLiveChunk({
        matchId,
        partNumber: partNo,
        chunkMs:    CHUNK_MS,
        blob,
      })
        .then(() => trimOldestChunks(matchId))
        .catch((err) => console.warn("live chunk IDB save failed", err));
    }
    const recId = recordingId.current;
    const key = `${recId}::${partNo}`;
    const p = (async () => {
      try {
        await idbPut({ key, recordingId: recId, partNumber: partNo, blob });
        await uploadChunk(recId, partNo, blob);
      } catch (err) {
        console.warn("chunk upload failed, kept in IDB", err);
      }
    })();
    pendingProm.current.push(p);
  }, [matchId, uploadChunk]);

  // Minimum wall-clock interval between v2 cycle starts. Prevents the
  // tight-spawn loop where MediaRecorder.onerror + onstop both fire
  // and each schedules another cycle — operators reported buttons
  // "flickering" because the React main thread was thrashed spawning
  // hundreds of MediaRecorders per second.
  const V2_MIN_CYCLE_GAP_MS = 250;
  const V2_MAX_CONSECUTIVE_ERRORS = 5;

  // Wrapped scheduler — guarantees only ONE pending cycle exists at a
  // time, and enforces a minimum gap between cycles so even if the
  // underlying MediaRecorder fails immediately we don't burn the CPU.
  const scheduleNextV2Cycle = useCallback(() => {
    if (isStoppingRef.current || !stream) return;
    if (cyclePendingRef.current) return;   // ← single-cycle guard
    cyclePendingRef.current = true;
    const elapsed = performance.now() - lastCycleAtRef.current;
    const wait = Math.max(0, V2_MIN_CYCLE_GAP_MS - elapsed);
    cycleTimerRef.current = setTimeout(() => {
      cyclePendingRef.current = false;
      startNextV2Chunk();
    }, wait);
  }, [stream]);   // startNextV2Chunk captured via closure below

  const startNextV2Chunk = useCallback(() => {
    if (isStoppingRef.current || !stream) return;
    if (consecutiveCycleErrorsRef.current >= V2_MAX_CONSECUTIVE_ERRORS) {
      console.error(
        "v2 recorder bailed: %d consecutive cycle errors. " +
        "MediaRecorder isn't producing frames — camera off, codec mismatch, or browser bug.",
        consecutiveCycleErrorsRef.current,
      );
      setError("Recording stopped — camera isn't producing frames. Stop + restart.");
      setState("error");
      return;
    }
    lastCycleAtRef.current = performance.now();
    const mime = pickMime();
    partCounter.current += 1;
    const partNo = partCounter.current;
    let chunkPieces = [];
    let chained = false;   // ← only chain ONCE per recorder lifecycle (onerror OR onstop, not both)
    const chainOnce = () => {
      if (chained) return;
      chained = true;
      if (!isStoppingRef.current) scheduleNextV2Cycle();
    };

    let rec;
    try {
      rec = new MediaRecorder(stream, {
        mimeType: mime || undefined,
        videoBitsPerSecond: 1_500_000,
      });
    } catch (err) {
      console.error("MediaRecorder construction failed:", err?.message || err);
      consecutiveCycleErrorsRef.current += 1;
      chainOnce();
      return;
    }
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size) chunkPieces.push(e.data);
    };
    rec.onerror = (e) => {
      console.warn("v2 cycle recorder error:", e?.error?.name || "unknown");
      // DON'T chain from onerror — onstop fires right after and the
      // single-chain guard would also reset the consec counter. Just
      // bump errors here; chainOnce in onstop schedules the next cycle.
      consecutiveCycleErrorsRef.current += 1;
    };
    rec.onstop = () => {
      if (chunkPieces.length === 0) {
        consecutiveCycleErrorsRef.current += 1;
        console.warn("v2 chunk %d had zero data — likely no frames (consec errors: %d)",
                     partNo, consecutiveCycleErrorsRef.current);
      } else {
        consecutiveCycleErrorsRef.current = 0;   // reset on a healthy chunk
        const blob = new Blob(chunkPieces, {
          type: chunkPieces[0].type || mime || "video/mp4",
        });
        chunkPieces = [];
        handleV2ChunkBlob(partNo, blob);
      }
      chainOnce();
    };
    try {
      rec.start();
    } catch (err) {
      console.error("MediaRecorder.start failed:", err?.message || err);
      consecutiveCycleErrorsRef.current += 1;
      chainOnce();
      return;
    }
    recRef.current = rec;
    // Schedule the stop after CHUNK_MS. Cancellable via stop() if the
    // operator hits Stop early.
    cycleTimerRef.current = setTimeout(() => {
      try {
        if (rec.state === "recording") rec.stop();
      } catch (err) {
        console.warn("v2 cycle stop failed:", err?.message || err);
      }
    }, CHUNK_MS);
  }, [stream, handleV2ChunkBlob, scheduleNextV2Cycle]);

  const start = useCallback(async () => {
    if (!stream) { setError("no stream"); return; }
    setState("starting"); setError("");
    try {
      const startRes = await startRecording(matchId, tier);
      recordingId.current = startRes.recording_id;

      // ── B2B path — AWS IVS Web Broadcast SDK ─────────────────────────
      if (startRes.tier === "B2B") {
        // Dynamic import keeps the ~500 KB SDK out of the B2C bundle.
        const mod = await import("amazon-ivs-web-broadcast");
        const IVS = mod.default || mod;

        const client = IVS.create({
          streamConfig: IVS.STANDARD_LANDSCAPE,
          ingestEndpoint: startRes.ivs_ingest_endpoint,
        });
        // The browser MediaStream is split into its video + audio tracks
        // and registered as input devices on the client. The SDK handles
        // encoding + the WebRTC handshake.
        const vTrack = stream.getVideoTracks()[0] || null;
        const aTrack = stream.getAudioTracks()[0] || null;
        if (vTrack) client.addVideoInputDevice(vTrack, "camera1", { index: 0 });
        if (aTrack) client.addAudioInputDevice(aTrack, "mic1");

        client.on("ACTIVE_STATE_CHANGE", (active) => {
          if (active) setState("recording");
        });
        client.on("ERROR", (e) => {
          setError(`IVS error: ${e?.code || ""} ${e?.message || e}`);
          setState("error");
        });

        await client.startBroadcast(startRes.ivs_stream_key);
        ivsRef.current = client;
        startedAtRef.current = performance.now();
        accumMsRef.current   = 0;
        setState("recording");
        return;
      }

      // ── B2C path ─────────────────────────────────────────────────────
      // recorder_version comes from the backend's start response:
      //   v2 (default for new recordings) = 5s standalone-chunk PUTs,
      //                                     EC2 master concat at Stop.
      //   v1 (only for resumed-old-recording) = legacy multipart upload.
      recorderVersionRef.current = startRes.recorder_version || 2;
      partCounter.current   = 0;
      uploadedRef.current   = [];
      inFlightRef.current   = new Set();
      pendingProm.current   = [];
      isStoppingRef.current = false;
      liveBytes.current     = 0;
      totalBytes.current    = 0;
      setFinalizeState(null);
      // Clear any prior live chunks for THIS match before recording
      // fresh ones. AWAITED so a fast Stop (< timeslice) can't race
      // with an outstanding delete that wipes a chunk we just wrote.
      if (matchId) {
        try { await clearLiveChunks(matchId); }
        catch (err) { console.warn("clearLiveChunks failed (continuing)", err); }
      }

      // resume any orphaned chunks from a prior crash for this recording
      const orphans = await idbAllForRec(startRes.recording_id);
      for (const o of orphans) {
        partCounter.current = Math.max(partCounter.current, o.partNumber);
        try { await uploadChunk(startRes.recording_id, o.partNumber, o.blob); } catch {}
      }

      // If the video track ends mid-recording (camera unplugged, OS
      // permission revoked, browser tab evicted), MediaRecorder will
      // keep firing dataavailable but with EMPTY frames — the master
      // ends up as init-segment-only (1-2 KB) and the slicer has
      // nothing to cut. Surface the failure immediately so the operator
      // can re-start instead of recording 90 minutes of nothing.
      const vTrack = stream.getVideoTracks()[0] || null;
      if (vTrack) {
        vTrack.addEventListener("ended", () => {
          console.error("Video track ended while recording — no frames will be captured.");
          setError("Camera stopped — recording can't capture frames. Stop + restart.");
          setState("error");
        });
      }

      startedAtRef.current = performance.now();
      accumMsRef.current   = 0;
      setState("recording");

      const isV2 = (recorderVersionRef.current || 2) >= 2;
      if (isV2) {
        // V2 path — stop/start cycle producing complete MP4 chunks.
        // Each chunk has its own ftyp+moov+mdat so the slicer can
        // decode it standalone. No init-segment plumbing needed.
        // Reset cycle-loop counters before kicking off.
        cyclePendingRef.current = false;
        consecutiveCycleErrorsRef.current = 0;
        lastCycleAtRef.current = 0;
        scheduleNextV2Cycle();
      } else {
        // V1 legacy path — single MediaRecorder with timeslice.
        // Chunks 2+ lack init segments but v1's only consumer
        // (MediaConvert against the finalised multipart-uploaded
        // master) doesn't care because it sees the assembled MP4.
        const mime = pickMime();
        const rec  = new MediaRecorder(stream, {
          mimeType: mime || undefined,
          videoBitsPerSecond: 1_500_000,
        });
        rec.ondataavailable = onDataAvailable;
        rec.onerror = (e) => { setError(`recorder error: ${e.error?.name || "unknown"}`); setState("error"); };
        recRef.current = rec;
        rec.start(CHUNK_MS);
      }
    } catch (err) {
      setError(err.message || "failed to start");
      setState("error");
    }
  }, [matchId, stream, tier, onDataAvailable, uploadChunk, scheduleNextV2Cycle]);

  // Resume a recording that was started before this page mount. Used
  // when the operator refreshes / reloads while recording — instead of
  // starting fresh (which would orphan the S3 parts uploaded
  // pre-refresh + reset video_start_ms to 0), we fetch the existing
  // MatchRecording's state (parts list + next part number + elapsed
  // estimate) and start MediaRecorder pointed at the SAME multipart
  // upload, with partCounter advanced past the existing parts and
  // accumMsRef set so the next ball coded has the right
  // video_start_ms on the master timeline.
  //
  // Idempotent: safe to call multiple times — exits early if state
  // isn't "idle" (already recording, or starting, or no-op).
  // Silently no-ops if the backend says no active recording exists.
  const resume = useCallback(async () => {
    if (state !== "idle") return false;
    if (!stream || !matchId) return false;
    let active;
    try {
      active = await getActiveRecording(matchId);
    } catch (err) {
      // 404 = no in-flight recording — that's not an error, just a
      // signal to fall through to a normal Start flow on operator click.
      if (err?.response?.status === 404) return false;
      console.warn("active-recording check failed:", err?.message || err);
      return false;
    }
    if (!active || active.tier !== "B2C") {
      // B2B/IVS resume is a different beast — the IVS broadcast
      // session can't be picked up mid-stream from a new tab. Leave
      // those to a fresh Start.
      return false;
    }
    setState("starting"); setError("");
    try {
      recordingId.current = active.recording_id;
      // Inherit the recorder protocol version from the in-flight
      // recording — we MUST NOT switch v1 ↔ v2 mid-recording or
      // we'd orphan chunks under a key shape the other path can't
      // assemble.
      recorderVersionRef.current = active.recorder_version || 1;
      uploadedRef.current = (active.parts || []).map((p) => ({
        PartNumber: p.PartNumber,
        ETag: p.ETag || "",
      }));
      partCounter.current = (active.next_part_number || 1) - 1;
      // Wall-clock elapsed_ms estimate from completed parts. Used for
      // the next ball's video_start_ms — the refresh gap (camera
      // re-acquisition takes ~1-3s and can't be recorded) is folded
      // into "the operator just lost a few seconds of footage" which
      // we accept as the cost of refresh-tolerance.
      accumMsRef.current = active.elapsed_ms_estimate || 0;
      setElapsedMs(accumMsRef.current);
      totalBytes.current = 0;
      inFlightRef.current = new Set();
      pendingProm.current = [];
      isStoppingRef.current = false;
      liveBytes.current = 0;

      // Resume orphan chunks from IDB (chunks that were queued but
      // didn't finish their S3 PUT before the tab closed). These have
      // partCounter <= our current cursor; uploadChunk is idempotent
      // (S3 overwrites if the part already landed).
      const orphans = await idbAllForRec(active.recording_id);
      for (const o of orphans) {
        partCounter.current = Math.max(partCounter.current, o.partNumber);
        try { await uploadChunk(active.recording_id, o.partNumber, o.blob); } catch {}
      }

      const vTrack = stream.getVideoTracks()[0] || null;
      if (vTrack) {
        vTrack.addEventListener("ended", () => {
          console.error("Video track ended while recording — no frames will be captured.");
          setError("Camera stopped — recording can't capture frames. Stop + restart.");
          setState("error");
        });
      }

      startedAtRef.current = performance.now();
      setState("recording");
      console.info(
        "[recorder] resumed recording %s — %d parts already uploaded, next part %d, elapsed ~%dms",
        active.recording_id, (active.parts || []).length,
        partCounter.current + 1, accumMsRef.current,
      );

      const isV2 = (recorderVersionRef.current || 1) >= 2;
      if (isV2) {
        cyclePendingRef.current = false;
        consecutiveCycleErrorsRef.current = 0;
        lastCycleAtRef.current = 0;
        scheduleNextV2Cycle();
      } else {
        const mime = pickMime();
        const rec = new MediaRecorder(stream, {
          mimeType: mime || undefined,
          videoBitsPerSecond: 1_500_000,
        });
        rec.ondataavailable = onDataAvailable;
        rec.onerror = (e) => { setError(`recorder error: ${e.error?.name || "unknown"}`); setState("error"); };
        recRef.current = rec;
        rec.start(CHUNK_MS);
      }
      return true;
    } catch (err) {
      setError(err.message || "resume failed");
      setState("error");
      return false;
    }
  }, [state, stream, matchId, onDataAvailable, uploadChunk, scheduleNextV2Cycle]);

  const stop = useCallback(async () => {
    if (state !== "recording") return;
    setState("stopping");
    accumMsRef.current += performance.now() - startedAtRef.current;

    // ── B2B path — stop the IVS broadcast + let the backend tear down
    //    the channel. There's no multipart to finalize; the IVS recording
    //    finishes asynchronously to S3 over the next ~30s. ────────────
    if (ivsRef.current) {
      try {
        await ivsRef.current.stopBroadcast();
        ivsRef.current.delete();
      } catch (err) {
        console.warn("IVS stop failed (continuing)", err);
      }
      ivsRef.current = null;
      try {
        await completeRecording(recordingId.current, {
          duration_ms: Math.round(accumMsRef.current),
          byte_size: 0,  // IVS reports the actual size async; not known here
        });
        setState("complete");
      } catch (err) {
        setError(err.message);
        setState("error");
      }
      return;
    }

    // ── B2C path — flush MediaRecorder then finalize ─────────────────
    isStoppingRef.current = true;
    // Cancel the pending v2 cycle timer so the next cycle isn't
    // started after we stop the current one. Also clear the pending
    // flag so any chainOnce() that fires from a late onstop sees
    // isStoppingRef=true AND finds no pending cycle to spawn.
    if (cycleTimerRef.current) {
      clearTimeout(cycleTimerRef.current);
      cycleTimerRef.current = null;
    }
    cyclePendingRef.current = false;
    // Flush the current MediaRecorder. For v2 the current recorder is
    // a 5s cycle that will fire its onstop → upload the chunk; we set
    // isStoppingRef=true above so onstop won't chain to a next cycle.
    // For v1 it's the single long recorder which we stop normally.
    const flushed = new Promise((res) => {
      const existing = recRef.current?.onstop;
      if (recRef.current) {
        recRef.current.onstop = (ev) => {
          try { existing && existing(ev); } catch {}
          res();
        };
      } else {
        res();
      }
    });
    try {
      if (recRef.current && recRef.current.state === "recording") {
        // v1 only — requestData forces the final media flush. Cycle
        // recorders flush implicitly on stop, no requestData needed.
        const isV2 = (recorderVersionRef.current || 2) >= 2;
        if (!isV2) recRef.current.requestData();
        recRef.current.stop();
      }
    } catch (err) {
      console.warn("stop failed:", err?.message || err);
    }
    await flushed;
    if (pendingProm.current.length > 0) {
      await Promise.allSettled(pendingProm.current);
      pendingProm.current = [];
    }

    const isV2 = (recorderVersionRef.current || 2) >= 2;

    if (isV2) {
      // v2 finalize — kick off the EC2 master concat and poll state.
      setFinalizeState({ phase: "queued", pct: 0, error: "" });
      try {
        await finalizeRecording(recordingId.current, {
          duration_ms: Math.round(accumMsRef.current),
        });
      } catch (err) {
        setFinalizeState({
          phase: "failed",
          pct: 0,
          error: err?.response?.data?.detail || err?.message || "finalize failed",
        });
        setState("error");
        return;
      }
      // Poll every 2s until complete or failed. The Stop overlay
      // renders finalizeState; the operator can't dismiss it until
      // we flip to COMPLETE (or surface an error).
      const recId = recordingId.current;
      const pollOnce = async () => {
        try {
          const s = await getRecordingState(recId);
          setFinalizeState({
            phase: s.finalize_phase,
            pct:   s.progress_pct || 0,
            error: s.error || "",
          });
          if (s.finalize_phase === "complete" && s.state === "COMPLETE") {
            setState("complete");
            return true;
          }
          if (s.finalize_phase === "failed") {
            setState("error");
            setError(s.error || "master concat failed");
            return true;
          }
        } catch (err) {
          // Transient network blip — keep polling.
          console.warn("recording state poll failed:", err?.message || err);
        }
        return false;
      };
      // Initial fast poll, then 2s cadence.
      const loop = async () => {
        // 10-minute cap — covers worst-case 3hr-match concat + upload.
        for (let i = 0; i < 300; i++) {
          if (await pollOnce()) return;
          await new Promise((r) => setTimeout(r, 2000));
        }
        setError("Master finalisation timed out — check back in a few minutes.");
        setState("error");
      };
      loop();
      return;
    }

    // v1 legacy finalize (multipart complete).
    if (uploadedRef.current.length === 0) {
      setError(
        "No video chunks were uploaded — camera may not have produced "
        + "any frames. Try restarting the recording.",
      );
      setState("error");
      return;
    }
    try {
      await completeRecording(recordingId.current, {
        parts:       uploadedRef.current.sort((a,b) => a.PartNumber - b.PartNumber),
        duration_ms: Math.round(accumMsRef.current),
        byte_size:   totalBytes.current,
      });
      setState("complete");
    } catch (err) {
      setError(err.message);
      setState("error");
    }
  }, [state]);

  const cancel = useCallback(async () => {
    isStoppingRef.current = true;
    if (cycleTimerRef.current) {
      clearTimeout(cycleTimerRef.current);
      cycleTimerRef.current = null;
    }
    cyclePendingRef.current = false;
    if (ivsRef.current) {
      try { await ivsRef.current.stopBroadcast(); ivsRef.current.delete(); } catch {}
      ivsRef.current = null;
    }
    if (recRef.current && recRef.current.state !== "inactive") {
      try { recRef.current.stop(); } catch {}
    }
    if (recordingId.current) {
      try { await abortRecording(recordingId.current); } catch {}
    }
    setState("idle");
  }, []);

  // ── per-ball clock — what BallByBallPage reads instead of useObsTimeline ──
  const videoTimestampMs = elapsedMs;
  const isLive = state === "recording";

  return {
    state,
    error,
    elapsedMs,
    videoTimestampMs,
    isLive,
    uploadProgress,
    recordingId: recordingId.current,
    recorderVersion: recorderVersionRef.current,
    finalizeState,
    start, stop, cancel, resume,
  };
}
